import React, { useState, useEffect } from 'react';
import { CustomDialog } from '../../Component/Dialog';
import {
  Grid,
  Button,
  ButtonGroup,
  TextField,
  Select,
  MenuItem,
  Tooltip,
  InputAdornment,
  Divider,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import checkKey from '../../../utils/CheckKey';
import PropTypes from 'prop-types';

export const FormComponentSettings = props => {
  const {
    openComponentSettings,
    setOpenComponentSettings,
    exposeMeta,
    prefixLabel,
    formSubmittedDateFormat,
    formId,
    setExposeMeta,
    setPrefixLabel,
    setPrefixLabelErrorMsg,
    handleSubmit,
    setFormSubmittedDateFormat,
    // Component metadata props
    metadataEnabled,
    startTimestampLabel,
    endTimestampLabel,
    metadataTimestampFormat,
    setMetadataEnabled,
    setStartTimestampLabel,
    setEndTimestampLabel,
    setMetadataTimestampFormat,
  } = props;

  // Local state for metadata validation
  const [startLabelError, setStartLabelError] = useState('');
  const [endLabelError, setEndLabelError] = useState('');

  // Validate label - only alphanumeric and underscores allowed
  const validateLabel = (value) => {
    if (!value || value.length === 0) {
      return 'This field cannot be empty';
    }
    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      return 'No special characters or spaces allowed';
    }
    return '';
  };

  // Generate default labels based on component ID
  const generateDefaultLabels = () => {
    const last4Chars = formId ? formId.slice(-4) : '';
    if (setStartTimestampLabel) setStartTimestampLabel(`startTimestamp_${last4Chars}`);
    if (setEndTimestampLabel) setEndTimestampLabel(`endTimestamp_${last4Chars}`);
  };

  const handleEnableMetadata = (enabled) => {
    if (setMetadataEnabled) setMetadataEnabled(enabled);
    if (enabled && !startTimestampLabel && !endTimestampLabel) {
      generateDefaultLabels();
    }
  };

  const handleStartLabelChange = (e) => {
    const value = e.target.value;
    if (setStartTimestampLabel) setStartTimestampLabel(value);
    setStartLabelError(validateLabel(value));
  };

  const handleEndLabelChange = (e) => {
    const value = e.target.value;
    if (setEndTimestampLabel) setEndTimestampLabel(value);
    setEndLabelError(validateLabel(value));
  };

  const isMetadataSaveDisabled = metadataEnabled && 
    (startLabelError || endLabelError || !startTimestampLabel || !endTimestampLabel);

  return (
    <CustomDialog
      isOpen={openComponentSettings}
      handleClose={() => {
        setOpenComponentSettings(false);
      }}
      title={<>Settings</>}
      contents={
        <>
          <Grid container item xs={12} direction={'column'}>
            <Grid container item xs={12} direction={'row'} alignItems={'center'}>
              <p className={'s-text'}>Component ID </p>
              <Tooltip
                title="This is a unique identification code for this component."
                placement="right"
              >
                <HelpOutlineIcon className={'custom-icon-btn'} />
              </Tooltip>
            </Grid>

            <Grid item xs={12}>
              {formId}
            </Grid>
            <Grid container item xs={12} direction={'row'} alignItems={'center'}>
              <p className={'s-text'}>Set placeholder prefix</p>
              <Tooltip
                title="Set a prefix for this component's exported placeholders."
                placement="right"
              >
                <HelpOutlineIcon className={'custom-icon-btn'} />
              </Tooltip>
            </Grid>
            <ButtonGroup>
              <Button
                className="d-button"
                color="primary"
                variant={exposeMeta ? 'contained' : 'outlined'}
                onClick={() => {
                  setExposeMeta(true);
                  // Use last 4 characters of the formId
                  const last4Chars = formId.slice(-4);
                  setPrefixLabel(`form_${last4Chars}`);
                }}
              >
                On
              </Button>
              <Button
                className="d-button"
                color="primary"
                variant={exposeMeta ? 'outlined' : 'contained'}
                onClick={() => {
                  setExposeMeta(false);
                  setPrefixLabel('');
                }}
              >
                Off
              </Button>
            </ButtonGroup>

            {exposeMeta && (
              <>
                <Grid container item xs={12} direction={'row'} alignItems={'center'}>
                  <p className={'s-text'}>Prefix </p>
                  <Tooltip
                    title="The component label is the prefix in front of all placeholders e.g. ${prefix.placeholder}."
                    placement="right"
                  >
                    <HelpOutlineIcon className={'custom-icon-btn'} />
                  </Tooltip>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    error={exposeMeta && (prefixLabel == '' || prefixLabel == null)}
                    fullWidth
                    defaultValue={''}
                    onChange={e => {
                      let value = undefined;
                      if (e.target.value && e.target.value.length > 0) {
                        value = checkKey(e);
                      }

                      if (value && value[0]) {
                        setPrefixLabelErrorMsg(value[0]);
                      } else {
                        setPrefixLabel(e.target.value);
                      }
                    }}
                    value={prefixLabel}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{'${'}</InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">{'.placeholder}'}</InputAdornment>
                      ),
                    }}
                  />
                  {exposeMeta && (prefixLabel == '' || prefixLabel == null) && (
                    <p className={'warning'}>Prefix value cannot be empty</p>
                  )}
                </Grid>
              </>
            )}
            <Grid container item xs={12} direction={'row'} alignItems={'center'}>
              <p className={'s-text'}>Timestamp format</p>
              <Tooltip
                title="This sets the timestamp format for formSubmittedDate"
                placement="right"
              >
                <HelpOutlineIcon className={'custom-icon-btn'} />
              </Tooltip>
            </Grid>

            <Grid item xs={12} className={'s-text'}>
              <Select
                onChange={e => {
                  setFormSubmittedDateFormat(e.target.value);
                }}
                variant={'outlined'}
                value={formSubmittedDateFormat}
                defaultValue={'dd MMMM yyyy HH:mm'}
              >
                <MenuItem value={"yyyy-MM-dd'T'HH:mm:ssX"}>
                  yyyy-mm-dd'T'HH:mm:ssZ (2013-09-29T18:46:19Z)
                </MenuItem>
                <MenuItem value={'dd MMMM yyyy HH:mm'}>
                  dd Month yyyy hh:mm (01 February 2021 14:01)
                </MenuItem>
                <MenuItem value={'dd/MM/yy HH:mm'}>dd/mm/yy hh:mm (15/12/21 14:01)</MenuItem>
                <MenuItem value={'dd/MM/yyyy HH:mm'}>dd/mm/yyyy hh:mm (15/12/2021 14:01)</MenuItem>
                <MenuItem value={'dd-MM-yyyy HH:mm'}>dd-mm-yyyy hh:mm (15-12-2021 14:01)</MenuItem>
                <MenuItem value={'MM/dd/yyyy HH:mm'}>mm/dd/yyyy hh:mm (12/15/2021 14:01)</MenuItem>
                <MenuItem value={'yyyy-MM-dd HH:mm'}>yyyy-mm-dd hh:mm (2021-02-01 14:01)</MenuItem>
              </Select>
            </Grid>

            {/* Component Metadata Section */}
            {setMetadataEnabled && (
              <>
                <Grid item xs={12} style={{ marginTop: '16px', marginBottom: '8px' }}>
                  <Divider />
                </Grid>

                <Grid container item xs={12} direction={'row'} alignItems={'center'}>
                  <p className={'s-text'}>Enable component metadata</p>
                  <Tooltip
                    title="Turn this on to expose component execution timestamps as placeholders that can be used in subsequent components."
                    placement="right"
                  >
                    <HelpOutlineIcon className={'custom-icon-btn'} />
                  </Tooltip>
                </Grid>
                <ButtonGroup>
                  <Button
                    className="d-button"
                    color="primary"
                    variant={metadataEnabled ? 'contained' : 'outlined'}
                    onClick={() => handleEnableMetadata(true)}
                  >
                    ON
                  </Button>
                  <Button
                    className="d-button"
                    color="primary"
                    variant={metadataEnabled ? 'outlined' : 'contained'}
                    onClick={() => handleEnableMetadata(false)}
                  >
                    OFF
                  </Button>
                </ButtonGroup>

                {metadataEnabled && (
                  <>
                    {/* Start Timestamp Label */}
                    <Grid container item xs={12} direction={'row'} alignItems={'center'}>
                      <p className={'s-text'}>Start timestamp placeholder</p>
                      <Tooltip
                        title="The placeholder name for when this component started executing."
                        placement="right"
                      >
                        <HelpOutlineIcon className={'custom-icon-btn'} />
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        value={startTimestampLabel || ''}
                        onChange={handleStartLabelChange}
                        error={!!startLabelError}
                        helperText={startLabelError}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">{'${'}</InputAdornment>,
                          endAdornment: <InputAdornment position="end">{'}'}</InputAdornment>,
                        }}
                      />
                    </Grid>

                    {/* End Timestamp Label */}
                    <Grid container item xs={12} direction={'row'} alignItems={'center'}>
                      <p className={'s-text'}>End timestamp placeholder</p>
                      <Tooltip
                        title="The placeholder name for when this component finished executing."
                        placement="right"
                      >
                        <HelpOutlineIcon className={'custom-icon-btn'} />
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        value={endTimestampLabel || ''}
                        onChange={handleEndLabelChange}
                        error={!!endLabelError}
                        helperText={endLabelError}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">{'${'}</InputAdornment>,
                          endAdornment: <InputAdornment position="end">{'}'}</InputAdornment>,
                        }}
                      />
                    </Grid>

                    {/* Metadata Timestamp Format */}
                    <Grid container item xs={12} direction={'row'} alignItems={'center'}>
                      <p className={'s-text'}>Metadata timestamp format</p>
                      <Tooltip
                        title="Select the format for the execution timestamp values."
                        placement="right"
                      >
                        <HelpOutlineIcon className={'custom-icon-btn'} />
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} className={'s-text'}>
                      <Select
                        variant="outlined"
                        fullWidth
                        value={metadataTimestampFormat || 'yyyy-MM-dd HH:mm:ss'}
                        onChange={(e) => setMetadataTimestampFormat && setMetadataTimestampFormat(e.target.value)}
                      >
                        <MenuItem value="yyyy-MM-dd'T'HH:mm:ssX">
                          yyyy-mm-dd'T'HH:mm:ssZ (2013-09-29T18:46:19Z)
                        </MenuItem>
                        <MenuItem value="dd MMMM yyyy HH:mm">
                          dd Month yyyy hh:mm (01 February 2021 14:01)
                        </MenuItem>
                        <MenuItem value="dd/MM/yy HH:mm">
                          dd/mm/yy hh:mm (15/12/21 14:01)
                        </MenuItem>
                        <MenuItem value="dd/MM/yyyy HH:mm">
                          dd/mm/yyyy hh:mm (15/12/2021 14:01)
                        </MenuItem>
                        <MenuItem value="dd-MM-yyyy HH:mm">
                          dd-mm-yyyy hh:mm (15-12-2021 14:01)
                        </MenuItem>
                        <MenuItem value="MM/dd/yyyy HH:mm">
                          mm/dd/yyyy hh:mm (12/15/2021 14:01)
                        </MenuItem>
                        <MenuItem value="yyyy-MM-dd HH:mm">
                          yyyy-mm-dd hh:mm (2021-02-01 14:01)
                        </MenuItem>
                        <MenuItem value="yyyy-MM-dd HH:mm:ss">
                          yyyy-mm-dd hh:mm:ss (2021-02-01 14:01:30)
                        </MenuItem>
                      </Select>
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
        </>
      }
      buttons={
        <>
          <Button
            onClick={() => {
              setOpenComponentSettings(false);
            }}
            variant={'outlined'}
          >
            {' '}
            Cancel
          </Button>

          <Button
            disabled={(exposeMeta == true && prefixLabel == '') || isMetadataSaveDisabled}
            onClick={() => {
              setPrefixLabel(prefixLabel);
              handleSubmit('published').then(() => {
                setOpenComponentSettings(false);
              });
            }}
            variant={'contained'}
            color={'primary'}
          >
            {' '}
            Save
          </Button>
        </>
      }
    />
  );
};
FormComponentSettings.propTypes = {
  openComponentSettings: PropTypes.bool.isRequired,
  setOpenComponentSettings: PropTypes.func.isRequired,
  exposeMeta: PropTypes.bool.isRequired,
  prefixLabel: PropTypes.string,
  formSubmittedDateFormat: PropTypes.string.isRequired,
  prefixLabelErrorMsg: PropTypes.string,
  formId: PropTypes.string.isRequired,
  setExposeMeta: PropTypes.func.isRequired,
  setPrefixLabel: PropTypes.func.isRequired,
  setPrefixLabelErrorMsg: PropTypes.func.isRequired,
  setFormSubmittedDateFormat: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  // Component metadata props
  metadataEnabled: PropTypes.bool,
  startTimestampLabel: PropTypes.string,
  endTimestampLabel: PropTypes.string,
  metadataTimestampFormat: PropTypes.string,
  setMetadataEnabled: PropTypes.func,
  setStartTimestampLabel: PropTypes.func,
  setEndTimestampLabel: PropTypes.func,
  setMetadataTimestampFormat: PropTypes.func,
};
